// Public stream viewer — '/streams/$streamId'.
//
// An anonymous Experience surface: the video leads, the interface recedes.
// One title (the stream name), one status badge, and copy written for a
// viewer rather than an operator. Portal metadata and playback URLs come
// from the anonymous public streams endpoint; the route parameter is the
// public RTMP_USERS id.
import { useRef } from 'react'
import {
  createFileRoute,
  ErrorComponent,
  Link,
  type ErrorComponentProps,
} from '@tanstack/react-router'
import { ArrowLeft, Copy, Share2 } from 'lucide-react'
import { toast } from 'sonner'

import { StreamPlayer } from '@/components/player/stream-player'
import { TranscriptPanel } from '@/components/transcript/TranscriptPanel'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { SectionCard } from '@/components/ui/section-card'
import { Skeleton } from '@/components/ui'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { liveFor, useStreamStatus } from '@/hooks/use-stream-status'

export const Route = createFileRoute('/streams/$streamId')({
  component: StreamViewerPage,
  errorComponent: StreamViewerError,
})

function StreamViewerPage() {
  const { streamId } = Route.useParams()
  const linkRef = useRef<HTMLInputElement | null>(null)

  const {
    data: streamMeta,
    error,
    isError,
    isLoading,
  } = useStreamStatus(streamId)

  if (isError) throw error

  const title = streamMeta?.name ?? streamId
  useDocumentTitle(streamMeta?.name ?? null)

  const isLive = streamMeta?.is_live ?? false
  const onAir = streamMeta ? liveFor(streamMeta) : null

  // The shareable portal uses its own public identifier. It is intentionally
  // independent from the private RTMP ingest token.
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const watchUrl = streamMeta?.watch_key
    ? `${origin}/live/${streamMeta.watch_key}`
    : null

  const copyLink = async () => {
    if (!watchUrl) {
      toast.error('Watch link unavailable')
      return
    }
    try {
      await navigator.clipboard.writeText(watchUrl)
      toast.success('Link copied')
    } catch {
      linkRef.current?.select()
      toast.error('Could not copy. The link is selected — copy it by hand.')
    }
  }

  const handleShare = async () => {
    if (!watchUrl) {
      toast.error('Watch link unavailable')
      return
    }
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title, url: watchUrl })
        return
      } catch (err) {
        // The viewer closed the share sheet: nothing to report.
        if (err instanceof DOMException && err.name === 'AbortError') return
      }
    }
    await copyLink()
  }

  return (
    <main
      data-testid="stream-viewer"
      className="mx-auto max-w-[1536px] px-4 py-6 sm:px-6 sm:py-8"
    >
      <Link
        to="/streams"
        className="mb-4 inline-flex min-h-9 items-center gap-2 rounded-md text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <ArrowLeft aria-hidden="true" className="size-4" />
        Back to streams
      </Link>

      <header className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1">
        {isLoading && !streamMeta ? (
          <Skeleton className="h-8 w-56" />
        ) : (
          <h1 className="text-xl font-semibold leading-tight sm:text-2xl">{title}</h1>
        )}
        <StatusBadge live={isLive} />
        {onAir ? (
          <span
            className="text-sm text-muted-foreground"
            title={onAir.since ?? undefined}
          >
            {onAir.label}
          </span>
        ) : null}
      </header>

      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div>
          <div
            className="overflow-hidden rounded-lg border border-border bg-black"
            data-testid="stream-viewer-player-card"
          >
            <StreamPlayer streamId={streamId} />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleShare}
              disabled={!watchUrl}
            >
              <Share2 aria-hidden="true" />
              Share stream
            </Button>
          </div>
        </div>

        <aside className="space-y-4">
          <SectionCard compact={true} title="Details">
            <dl className="space-y-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <dt className="text-muted-foreground">Status</dt>
                <dd className="text-right text-foreground">{isLive ? 'Live' : 'Offline'}</dd>
              </div>
              {onAir ? (
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-muted-foreground">On air</dt>
                  <dd className="text-right text-foreground" title={onAir.since ?? undefined}>
                    {onAir.label.replace(/^Live for /, '')}
                  </dd>
                </div>
              ) : null}
              {watchUrl ? (
                <div className="space-y-1.5 border-t border-border pt-3">
                  <dt>
                    <label htmlFor="stream-watch-link" className="text-muted-foreground">
                      Watch link
                    </label>
                  </dt>
                  <dd className="flex gap-2">
                    <Input
                      id="stream-watch-link"
                      ref={linkRef}
                      readOnly={true}
                      value={watchUrl}
                      onFocus={(event) => event.currentTarget.select()}
                      className="h-9 min-w-0 flex-1 font-mono text-[13px]"
                    />
                    <Button type="button" variant="outline" size="sm" onClick={copyLink}>
                      <Copy aria-hidden="true" />
                      Copy link
                    </Button>
                  </dd>
                </div>
              ) : null}
            </dl>
          </SectionCard>

          <TranscriptPanel
            watchKey={streamMeta?.watch_key ?? null}
            isLive={isLive}
          />
        </aside>
      </div>
    </main>
  )
}

function StatusBadge({ live }: { live: boolean }) {
  if (live) {
    return (
      <Badge variant="live" className="gap-1.5" data-testid="stream-status-badge">
        <span
          aria-hidden="true"
          className="relative inline-flex size-1.5 items-center justify-center"
        >
          <span className="animate-live-dot absolute inline-flex h-full w-full rounded-full bg-cyber-green" />
        </span>
        LIVE
      </Badge>
    )
  }
  return (
    <Badge variant="outline" className="border-border text-muted-foreground" data-testid="stream-status-badge">
      Offline
    </Badge>
  )
}

function StreamViewerError(props: ErrorComponentProps) {
  return (
    <main className="mx-auto max-w-[1536px] px-4 py-8 sm:px-6">
      <Card className="mx-auto max-w-2xl" data-testid="stream-viewer-error">
        <CardContent className="flex flex-col items-center gap-4 py-16 text-center sm:py-24">
          <img
            src="/assets/cyberpunk/cyberpunk-signal-lost.webp"
            alt=""
            aria-hidden={true}
            className="h-28 w-full max-w-md rounded-lg object-cover object-center opacity-70"
            loading="lazy"
          />
          <div className="space-y-2">
            <CardTitle className="text-2xl">Stream unavailable</CardTitle>
            <CardDescription className="text-sm text-muted-foreground">
              We couldn't open that stream. It may have ended or been removed.
            </CardDescription>
          </div>
          <div className="hidden text-xs text-muted-foreground sm:block">
            <ErrorComponent error={props.error} />
          </div>
          <Button asChild>
            <Link to="/streams">Back to streams</Link>
          </Button>
        </CardContent>
      </Card>
    </main>
  )
}
